import { type FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { toSafeInt } from "widgets/utils";

import { renderProteinAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.ts";
import { getPaddedRange } from "@/app-utils/range.ts";
import { type ProteinLocation, type Region } from "@/graphql.ts";
import { getClassicGenomeBrowserTracks } from "@/redux/modules/genome-browser.ts";
import { type IProteinState, shouldLoadProtein } from "@/redux/modules/protein.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { TRACK_TYPE } from "@/settings/settings.js";

import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import GenericEntityGenomeView from "../genome-viewer/GenericEntityGenomeView.js";
import { type IGenomeViewOwnProps } from "../genome-viewer/GenomeView.js";

const ProteinGenomeView: FC = () => {
  const { proteinId } = useParams<"proteinId">();
  const { protein } = useSelector<IAppState, IProteinState>((state) => state.protein);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadProtein, proteinId);

  useEffect(() => {
    void load();
  }, [load]);

  let genomeViewProps: IGenomeViewOwnProps;

  if (protein) {
    const locations: ProteinLocation[] = protein.proteinLocations;
    const region: Region =
      locations.length > 0 && locations[0].protein && locations[0].protein.id
        ? locations.find((t) => Number.parseInt(t.protein.id) === toSafeInt(proteinId)).region
        : locations[0].region;
    genomeViewProps = {
      genomeId: protein.genome.id,
      contigId: region.contig.id,
      range: getPaddedRange(region.range, region.contig.length),
      defaultTracks: getClassicGenomeBrowserTracks([TRACK_TYPE.SEQUENCE, TRACK_TYPE.GENE, TRACK_TYPE.PROTEIN_LOCATION]),
      genomes: [protein.genome],
      attributesWidgets: [
        <AttributesListWidget
          attributes={renderProteinAttributesWidgets(protein)}
          key={"protein-genome-view-widget-1"}
        />,
      ],
      title: protein?.sequenceId,
      subtitle: protein?.description || "Genome View",
    };
  }

  return <GenericEntityGenomeView genomeViewProps={genomeViewProps} loadEntityData={() => void load()} />;
};

export default ProteinGenomeView;
